/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

//LDS Checkout is Module containing checkout as a whole with HOC with all components involved with checkout.

import React, { useState } from 'react';
import styled from 'styled-components';
import { IInvoicePaymentSummary } from './components/get-invoice-payment-summary';
import { IOrderSummary } from './components/get-order-summary';
import { ILdsCheckoutViewProps } from './lds-checkout';
import { getRetailChannelAsync } from '../../actions/DataActionExtension.g';
//@ts-ignore
import { Error } from '@churchofjesuschrist/eden-alert';

const CheckoutContainer = styled.div`
    display: flex;
    flex-direction: row;
    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const OrderSummaryWrapper = styled.div`
    lineheight: 24px;
    font-family: Ensign:Sans;
    font-style: normal;
    font-weight: 700;
    font-size: 20px;
    color: #000000;
    margin-top: 148px;
    padding: 0 20px;
    @media (max-width: 768px) {
        margin-top: 20px;
    }
`;

const OrderSummaryComponent: React.FC<IOrderSummary> = ({ heading, lines }) => {
    const summaryHeadingStyle: React.CSSProperties = {
        lineHeight: '24px',
        fontFamily: `"Ensign:Sans",Arial,"noto sans" ,sans-serif`,
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: '20px',
        color: '#000000'
    };

    return (
        <OrderSummaryWrapper>
            <div style={summaryHeadingStyle}>{heading}</div>
            <div className='msc-order-summary__items'>
                {lines && (
                    <>
                        {lines.subtotal}
                        {lines.shipping}
                        {lines.otherCharge}
                        {lines.tax}
                        {lines.totalDiscounts}
                        {lines.loyalty}
                        {lines.giftCard}
                        {lines.orderTotal}
                    </>
                )}
            </div>
        </OrderSummaryWrapper>
    );
};

const PaymentSummaryComponent: React.FC<IInvoicePaymentSummary> = ({ heading, lines }) => (
    <div className='msc-invoice-summary-wrapper'>
        {heading}
        <div className='msc-invoice-summary__items'>
            {lines && (
                <>
                    {lines.invoices}
                    {lines.giftCard}
                    {lines.loyalty}
                    {lines.orderTotal}
                </>
            )}
        </div>
    </div>
);

export default (props: ILdsCheckoutViewProps) => {
    const {
        items,
        shippingCartLines,
        subscriptionLines,
        pickupLines,
        checkout,
        address,
        countryRegions,
        addressPurposes,
        countryStates,
        deliveryOptions,
        hasInvoiceLine,
        orderSummary,
        invoicePaymentSummary,
        customerSubscriptions,
        isPhoneRequired,
        saveShippingAddress,
        getSmartyAddress,
        setDeliveryOptionOnShippingCartLines,
        setContactOnShippingCartLines,
        updateCartAttributes,
        updateCurrentCart,
        checkoutError,
        updateCheckoutError
    } = props;
    const [activeIndex, setActiveIndex] = useState(0);
    const [disableCheckout, setDisableCheckout] = useState(0);
    const checkoutHeaderStyle: React.CSSProperties = {
        lineHeight: '60px',
        paddingRight: '16px',
        paddingLeft: '18px',
        fontFamily: `"Ensign:Sans",Arial,"noto sans" ,sans-serif`,
        fontStyle: 'normal',
        fontSize: '32px',
        color: '#000000',
        padding: '40px 0 50px 0'
    };
    const getRetailChannel = async () => {
        const ctx = { callerContext: props.context.actionContext };

        // @ts-ignore
        const channel = await getRetailChannelAsync(ctx, props.context.request.channel?.RecordId);
        setDisableCheckout(channel.DisableCheckout);
    };

    React.useEffect(() => {
        void getRetailChannel();
    }, []);

    const leftPanelStyle: React.CSSProperties = {
        flex: '3'
    };

    const checkoutSections: React.ReactNode[] = [];
    items.forEach(slot => {
        if (slot) {
            // @ts-ignore
            if (slot.props.typeName === 'lds-shipping') {
                if (shippingCartLines.length > 0) {
                    checkoutSections.push(slot);
                }
            } else {
                checkoutSections.push(slot);
            }
        }
    });
    //Disable Checkout is a server side call if returns 1 for that country checkout must be disabled
    if (disableCheckout === 1) {
        return (
            <div style={{ height: '65vh' }}>
                <div style={{ paddingTop: '20px' }}>
                    <Error>
                        {/* @ts-ignore */}
                        <div style={{ paddingTop: '3px' }}>{props.resources.checkoutDisabledText}</div>
                    </Error>
                </div>
            </div>
        );
    } else if (props.subscriptionLines[0]) {
        return (
            <div style={{ height: '65vh' }}>
                <div style={{ paddingTop: '20px' }}>
                    <Error>
                        {/* @ts-ignore */}
                        <div style={{ paddingTop: '3px' }}>{props.resources.checkoutDisabledSubscriptionText}</div>
                    </Error>
                </div>
            </div>
        );
    } else {
        return (
            <CheckoutContainer>
                <div style={leftPanelStyle}>
                    <div style={checkoutHeaderStyle}>{props.resources.checkoutLabel}</div>
                    {checkoutSections.map((item, index) => {
                        const extraProps = {
                            sectionActive: index === activeIndex,
                            sectionVisited: index < activeIndex,
                            sectionIndex: index,
                            nextSection: () => {
                                setActiveIndex(index + 1);
                            },
                            prevSection: () => {
                                setActiveIndex(index - 1);
                                updateCheckoutError(false);
                            },
                            shippingCartLines,
                            subscriptionLines,
                            pickupLines,
                            checkout,
                            address,
                            countryRegions,
                            addressPurposes,
                            countryStates,
                            deliveryOptions,
                            customerSubscriptions,
                            isPhoneRequired,
                            saveShippingAddress,
                            setDeliveryOptionOnShippingCartLines,
                            setContactOnShippingCartLines,
                            updateCartAttributes,
                            updateCurrentCart,
                            getSmartyAddress,
                            checkoutError
                        };

                        return (
                            <React.Fragment key={index}>
                                {/*
                            // @ts-ignore */}
                                {item && <item.type {...item.props} {...extraProps} />}
                            </React.Fragment>
                        );
                    })}
                </div>
                <div className='order-summary-checkout'>
                    {!hasInvoiceLine
                        ? orderSummary && <OrderSummaryComponent {...orderSummary} />
                        : invoicePaymentSummary && <PaymentSummaryComponent {...invoicePaymentSummary} />}
                </div>
            </CheckoutContainer>
        );
    }
};
